import { ApiKeyStamper } from '@turnkey/api-key-stamper';
import { TurnkeySigner } from '@turnkey/ethers';
import { TurnkeyClient } from '@turnkey/http';
import { ethers } from 'ethers';

const TURNKEY_BASE_URL = 'https://api.turnkey.com';

const USDC_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address account) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

export type TurnkeyAdminCredentials = {
  organizationId: string;
  apiPublicKey: string;
  apiPrivateKey: string;
  walletAddress: string;
};

const PROD_HOSTNAME = 'admin.domxbridgetower.com';

const MAINNET = {
  rpcUrl: 'https://polygon-rpc.com',
  usdcAddress: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
};

const TESTNET = {
  rpcUrl: 'https://rpc-amoy.polygon.technology',
  usdcAddress: '0x41e94eb019c0762f9bfcf9fb1e58725bfb0e7582',
};

function getNetworkConfig() {
  const isProd = window.location.hostname === PROD_HOSTNAME;
  return isProd ? MAINNET : TESTNET;
}

function createClient(creds: TurnkeyAdminCredentials): TurnkeyClient {
  const stamper = new ApiKeyStamper({
    apiPublicKey: creds.apiPublicKey,
    apiPrivateKey: creds.apiPrivateKey,
  });
  return new TurnkeyClient({ baseUrl: TURNKEY_BASE_URL }, stamper);
}

export async function getUsdcBalance(walletAddress: string): Promise<string> {
  const { rpcUrl, usdcAddress } = getNetworkConfig();
  const provider = new ethers.JsonRpcProvider(rpcUrl);
  const usdc = new ethers.Contract(usdcAddress, USDC_ABI, provider);
  const [balance, decimals] = await Promise.all([
    usdc.balanceOf(walletAddress),
    usdc.decimals(),
  ]);
  return ethers.formatUnits(balance, decimals);
}

export async function transferUsdc(
  creds: TurnkeyAdminCredentials,
  toAddress: string,
  humanAmount: string, // e.g. "10.5" — converted to 6-decimal USDC units
): Promise<string> {
  const { rpcUrl, usdcAddress } = getNetworkConfig();

  const client = createClient(creds);

  const turnkeySigner = new TurnkeySigner({
    client,
    organizationId: creds.organizationId,
    signWith: creds.walletAddress,
  });

  const provider = new ethers.JsonRpcProvider(rpcUrl);
  const signer = turnkeySigner.connect(provider);

  const usdc = new ethers.Contract(usdcAddress, USDC_ABI, signer);
  const decimals: bigint = await usdc.decimals();
  const amount = ethers.parseUnits(humanAmount, decimals);

  const tx = await usdc.transfer(toAddress, amount);
  const receipt = await tx.wait();
  return receipt.hash as string;
}
